import { Trans, useLingui } from '@lingui/react/macro'
import { billingHooks, visitHooks } from '$src/lib/trpcHooks'
import { Alert, Badge, Button, Card, Group, Stack, Text } from '@mantine/core'
import {
  INVOICE_FEES,
  INVOICING_FEE_START_DATE,
  type VisitWithRelations,
} from '@markku/types'
import { calculateFinalPrice, formatDateTime } from '@markku/utils'
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'

// Predicate for unbilled tasks with billable amount
const isUnbilledBillableTask = (task: {
  billingType: string | null
  price: string | null
  discountPercent: number | null
  paidAt: string | null
  billedAt: string | null
}) =>
  task.billingType === 'separately_invoiced' &&
  calculateFinalPrice(task.price, task.discountPercent) > 0 &&
  !task.paidAt &&
  !task.billedAt

export function VisitPaymentBanner() {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { data: visitsResponse } = visitHooks.useList({
    filters: {
      status: {
        eq: 'completed',
      },
      subscriptionId: {
        isNull: true,
      },
    },
    orderBy: 'completedAt',
    orderDirection: 'desc',
  })
  const { mutate: createCheckout, isPending } =
    billingHooks.useCreateVisitPaymentCheckout()

  // Feature flag check
  if (import.meta.env.VITE_ENABLE_ONE_TIME_PURCHASE_BILLING !== 'true') {
    return null
  }

  // Find first completed visit with unbilled separately invoiced tasks
  // Note: Type cast needed because list returns Visit but actually includes relations
  const unbilledVisit = (visitsResponse?.data as VisitWithRelations[])?.find(
    (visit) =>
      visit.status === 'completed' &&
      visit.completedAt &&
      !visit.subscriptionId &&
      visit.visitTasks?.some(isUnbilledBillableTask),
  )

  if (!unbilledVisit) return null

  const unbilledTasks = unbilledVisit.visitTasks?.filter(isUnbilledBillableTask)
  const paidTasks = unbilledVisit.visitTasks?.filter(
    (task) =>
      task.billingType === 'separately_invoiced' &&
      calculateFinalPrice(task.price, task.discountPercent) > 0 &&
      task.paidAt,
  )

  const partiallyPaid = (paidTasks?.length ?? 0) > 0

  const total = unbilledTasks?.reduce(
    (sum, task) => sum + calculateFinalPrice(task.price, task.discountPercent),
    0,
  )

  // Calculate deadline
  const deadline = dayjs(unbilledVisit.completedAt).add(48, 'hours')
  const isOverdue = dayjs().isAfter(deadline)

  // Hide banner once deadline has passed
  if (isOverdue) {
    return null
  }

  // Check if invoicing fee applies (visits created on/after start date)
  const feeApplies = unbilledVisit.createdAt
    ? new Date(unbilledVisit.createdAt) >= new Date(INVOICING_FEE_START_DATE)
    : false

  const handlePay = () => {
    createCheckout({ visitId: unbilledVisit.id })
  }

  const handleViewDetails = () => {
    navigate({ to: `/visits/${unbilledVisit.id}` })
  }

  return (
    <Card withBorder radius="md" p="md" bg="orange.0">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="xs">
              <Text fw={600} size="lg">
                {partiallyPaid ? t`Visit partially paid` : t`Unpaid visit`}
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              {formatDateTime(unbilledVisit.startTime)}
            </Text>
            {unbilledVisit.property?.address && (
              <Text size="sm" c="dimmed">
                {unbilledVisit.property.address.street},{' '}
                {unbilledVisit.property.address.postalCode}{' '}
                {unbilledVisit.property.address.city}
              </Text>
            )}
            {partiallyPaid && (
              <Text size="sm" c="orange" fw={500}>
                {paidTasks?.length} <Trans>tasks paid</Trans>,{' '}
                {unbilledTasks?.length} <Trans>remaining</Trans>
              </Text>
            )}
          </Stack>
        </Group>

        <Alert
          icon={<IconAlertTriangle size={16} />}
          color={feeApplies ? 'orange' : 'blue'}
          variant="light"
        >
          <Text size="sm" fw={500}>
            {feeApplies
              ? t`Pay in app before ${formatDateTime(deadline.toISOString())} and save ${INVOICE_FEES.EMAIL}€ invoicing fee`
              : t`Pay in app before ${formatDateTime(deadline.toISOString())}`}
          </Text>
        </Alert>

        <Group justify="space-between" align="center">
          <div>
            <Text size="sm" c="dimmed">
              {partiallyPaid ? t`Unpaid` : t`Total to pay`}
            </Text>
            <Text size="xl" fw={700} c="orange">
              {total?.toFixed(2)} €
            </Text>
          </div>
        </Group>

        <Stack gap="xs">
          <Text size="sm" fw={600} c="dimmed">
            {partiallyPaid ? t`Unpaid tasks:` : t`Completed tasks:`}
          </Text>
          {unbilledTasks?.map((task) => {
            const finalPrice = calculateFinalPrice(
              task.price,
              task.discountPercent,
            )
            return (
              <Group key={task.id} justify="space-between" wrap="nowrap">
                <Stack gap={4} style={{ flex: 1 }}>
                  <Text size="sm">{task.taskName}</Text>
                  {task.discountPercent != null && task.discountPercent > 0 && (
                    <Badge size="xs" variant="light" color="green">
                      {task.discountPercent}% <Trans>discount</Trans>
                    </Badge>
                  )}
                </Stack>
                <Text size="sm" fw={500}>
                  {finalPrice.toFixed(2)}€
                </Text>
              </Group>
            )
          })}
        </Stack>

        <Group gap="sm">
          <Button
            color="orange"
            size="md"
            onClick={handlePay}
            loading={isPending}
            fullWidth
            leftSection={<IconCheck size={16} />}
          >
            <Trans>Pay now</Trans>
          </Button>
          <Button
            variant="light"
            color="orange"
            size="md"
            onClick={handleViewDetails}
          >
            <Trans>Show details</Trans>
          </Button>
        </Group>
      </Stack>
    </Card>
  )
}
